import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from '../shared/Spinner';
import { invoicesApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';

const ACCEPTED_MIME = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
const ACCEPTED_EXT = '.pdf,.png,.jpg,.jpeg';
const MAX_BYTES = 10 * 1024 * 1024;

type EntryStatus =
  | 'a_envoyer' // valide, en attente d'envoi
  | 'invalide' // rejeté côté client (type/taille) → non envoyé
  | 'en_cours' // upload en cours
  | 'importee' // 202 OK
  | 'doublon' // 409 déjà importée
  | 'erreur'; // 4xx/5xx/réseau

interface Entry {
  key: number;
  file: File;
  status: EntryStatus;
  message?: string;
  existingId?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Appelé après CHAQUE upload réussi → le parent invalide la liste (elle se
   *  peuple en direct). Pas de navigation : on reste sur la liste. */
  onChanged: () => void;
}

// Compteur de clés local (unicité de la liste ; réinitialisé à chaque reset).
let keySeq = 0;

// Attente utilitaire (retry 429).
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function UploadModal({ open, onClose, onChanged }: Props) {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false); // un lot a été envoyé → affiche récap
  const [sentCount, setSentCount] = useState(0); // progression X/N pendant l'envoi
  const [batchTotal, setBatchTotal] = useState(0); // nb de fichiers du lot en cours
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  if (!open) return null;

  const reset = () => {
    setEntries([]);
    setBusy(false);
    setDone(false);
    setSentCount(0);
    setBatchTotal(0);
    setDragOver(false);
  };

  const close = () => {
    if (busy) return; // ne pas fermer en plein envoi
    reset();
    onClose();
  };

  // Ajoute des fichiers à la liste (cumul des dépôts successifs). Validation
  // client par fichier : un fichier invalide entre dans la liste marqué
  // 'invalide' (visible) mais ne sera pas envoyé.
  const addFiles = (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    setDone(false);
    setEntries((prev) => {
      const next = [...prev];
      for (const file of arr) {
        let status: EntryStatus = 'a_envoyer';
        let message: string | undefined;
        if (!ACCEPTED_MIME.includes(file.type.toLowerCase())) {
          status = 'invalide';
          message = `Type non supporté (${file.type || 'inconnu'})`;
        } else if (file.size > MAX_BYTES) {
          status = 'invalide';
          message = `Trop volumineux (${Math.round(file.size / 1024 / 1024)} Mo > 10 Mo)`;
        }
        next.push({ key: keySeq++, file, status, message });
      }
      return next;
    });
  };

  const removeEntry = (key: number) => {
    setEntries((prev) => prev.filter((e) => e.key !== key));
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
  };

  const patch = (key: number, p: Partial<Entry>) =>
    setEntries((prev) => prev.map((e) => (e.key === key ? { ...e, ...p } : e)));

  // Upload UN fichier avec un retry unique sur 429 (rate-limit /api/*).
  const uploadWithRetry = async (file: File) => {
    let out = await invoicesApi.upload(file);
    if (!out.ok && out.kind === 'error' && out.httpStatus === 429) {
      await sleep(3000);
      out = await invoicesApi.upload(file);
    }
    return out;
  };

  const submit = async () => {
    const toSend = entries.filter((e) => e.status === 'a_envoyer');
    if (toSend.length === 0) return;
    setBusy(true);
    setSentCount(0);
    setBatchTotal(toSend.length);

    let imported = 0;
    let duplicates = 0;
    let errors = 0;

    // Séquentiel : progression déterministe + doux pour le rate-limit et pour
    // les extractions Claude déclenchées en arrière-plan.
    for (const [i, entry] of toSend.entries()) {
      patch(entry.key, { status: 'en_cours', message: undefined });
      const out = await uploadWithRetry(entry.file);
      if (out.ok) {
        imported++;
        patch(entry.key, { status: 'importee' });
        onChanged(); // la liste se peuple en direct
      } else if (out.kind === 'duplicate') {
        duplicates++;
        patch(entry.key, { status: 'doublon', message: out.message, existingId: out.existingId });
      } else {
        errors++;
        patch(entry.key, { status: 'erreur', message: out.message });
      }
      setSentCount(i + 1);
    }

    setBusy(false);
    setDone(true);

    const parts = [`${imported} importée${imported > 1 ? 's' : ''}`];
    if (duplicates) parts.push(`${duplicates} doublon${duplicates > 1 ? 's' : ''}`);
    if (errors) parts.push(`${errors} erreur${errors > 1 ? 's' : ''}`);
    const summary = parts.join(', ');
    if (errors) toast.error(summary);
    else if (duplicates) toast.info(summary);
    else toast.success(summary);
  };

  const pendingCount = entries.filter((e) => e.status === 'a_envoyer').length;

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4"
      onClick={close}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold">Ajouter des factures</h2>
        <p className="text-sm text-slate-500 mt-1">
          Un ou plusieurs fichiers PDF, PNG ou JPG (max 10 Mo chacun). L'extraction
          Claude se lance après l'envoi ; retrouvez-les dans la liste (onglet
          « Extraite ») pour les valider plus tard.
        </p>

        {/* Drag & drop / sélection multiple */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => !busy && inputRef.current?.click()}
          className={`mt-4 rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
            busy ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
          } ${
            dragOver
              ? 'border-blue-500 bg-blue-50'
              : 'border-slate-300 bg-slate-50 hover:border-slate-400'
          }`}
        >
          <div className="text-slate-600">
            <p className="text-2xl mb-1">📤</p>
            <p className="text-sm">
              Glisse un ou plusieurs fichiers ici
              <br />
              <span className="text-xs text-slate-500">ou clique pour parcourir</span>
            </p>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_EXT}
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files?.length) addFiles(e.target.files);
              e.target.value = ''; // permet de re-déposer les mêmes fichiers
            }}
          />
        </div>

        {/* Liste des fichiers + statut par fichier */}
        {entries.length > 0 && (
          <ul className="mt-4 max-h-64 overflow-auto divide-y divide-slate-100 rounded-md ring-1 ring-slate-200">
            {entries.map((e) => (
              <li key={e.key} className="flex items-center gap-2 px-3 py-2 text-sm">
                <span className="flex-1 min-w-0">
                  <span className="block truncate font-medium text-slate-800">
                    {e.file.name}
                  </span>
                  <span className="text-xs text-slate-500">
                    {(e.file.size / 1024).toFixed(0)} Ko
                    {e.message ? ` · ${e.message}` : ''}
                    {e.status === 'doublon' && e.existingId && (
                      <>
                        {' · '}
                        <Link
                          to={`/factures/liste/${e.existingId}`}
                          className="text-blue-600 hover:underline"
                          onClick={() => close()}
                        >
                          ouvrir l'existante
                        </Link>
                      </>
                    )}
                  </span>
                </span>
                <StatusChip status={e.status} />
                {!busy && e.status !== 'en_cours' && (
                  <button
                    type="button"
                    onClick={() => removeEntry(e.key)}
                    className="text-slate-400 hover:text-slate-700 text-lg leading-none"
                    title="Retirer"
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="mt-5 flex items-center justify-between gap-2">
          <span className="text-xs text-slate-500">
            {busy
              ? `Envoi… ${sentCount}/${batchTotal}`
              : entries.length > 0
                ? `${pendingCount} à envoyer${entries.length - pendingCount > 0 ? ` · ${entries.length - pendingCount} traité(s)/ignoré(s)` : ''}`
                : ''}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={close}
              disabled={busy}
              className="px-3 py-1.5 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              {done ? 'Fermer' : 'Annuler'}
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy || pendingCount === 0}
              className="px-3 py-1.5 rounded-md text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {busy && <Spinner />}
              {busy
                ? 'Envoi…'
                : pendingCount > 1
                  ? `Envoyer (${pendingCount})`
                  : 'Envoyer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusChip({ status }: { status: EntryStatus }) {
  const map: Record<EntryStatus, { label: string; cls: string }> = {
    a_envoyer: { label: 'À envoyer', cls: 'bg-slate-100 text-slate-600' },
    invalide: { label: 'Invalide', cls: 'bg-amber-100 text-amber-800' },
    en_cours: { label: 'Envoi…', cls: 'bg-blue-100 text-blue-800' },
    importee: { label: '✓ Importée', cls: 'bg-emerald-100 text-emerald-800' },
    doublon: { label: 'Doublon', cls: 'bg-amber-100 text-amber-800' },
    erreur: { label: '✗ Erreur', cls: 'bg-red-100 text-red-800' },
  };
  const { label, cls } = map[status];
  return (
    <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}
